import React, { useEffect, useState, useRef, useContext } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import theme from 'live/services/theme'
import moment from 'moment'
import 'moment-duration-format'

import Highcharts from 'highcharts'
import TripsContext from '../../contexts/TripsContext'
import { DashboardContext } from 'live/context/dashboard'
import { sort } from '../../utils'
import { getSummary } from '../../api/fleet'

const tz = Intl.DateTimeFormat().resolvedOptions().timeZone

const optionsChartLine: any = {
    time: { timezone: tz },
    margin: [0, 0, 0, 0],
    title: { text: '' },
    subtitle: { text: '' },

    yAxis: {
        lineWidth: 1,
        tickWidth: 1,
        title: {
            text: ''
            // align: 'high',
            // rotation: 0,
            // y: -15,
            // offset: -65
        },
        gridLineWidth: 0,
        min: 0
    },
    xAxis: {
        type: 'datetime',
        dateTimeLabelFormats: {
            day: '%H:%M'
        }
    },
    legend: { enabled: false },
    credits: { enabled: false },

    series: [],

    polygonOptions: {
        animationLimit: 1,
        series: {
            turboThreshold: 100000
        }
    },
    plotOptions: {
        series: {
            marker: {
                enabled: true
            }
        }
    }
}

const height = 180

const toChartOptions = (wayPoints: any[]) => {
    const options: any = {
        series: []
    }

    const data: any[] = []

    wayPoints.forEach(({ timestamp, speed }) => {
        const aux = moment(timestamp)
        data.push([
            Date.UTC(aux.year(), aux.month(), aux.date(), aux.hour(), aux.minute(), aux.second(), aux.millisecond()),
            +speed
        ])
    })

    options.series = [{ findNearestPointBy: 'xy', data, color: '#006EAC', name: i18n('Speed') }]

    return options
}

const timespanInformationToOptions = (speedList = []) => {
    const options: any = {
        series: []
    }

    const data: any[] = []
    speedList?.forEach(({ timestamp, avarageSpeed }) => {
        const aux = moment(timestamp)
        data.push([
            Date.UTC(aux.year(), aux.month(), aux.date(), aux.hour(), aux.minute(), aux.second(), aux.millisecond()),
            +avarageSpeed
        ])
    })

    options.series = [{ findNearestPointBy: 'xy', data, color: '#006EAC', name: i18n('Speed') }]

    return options
}


const mountSummary = ({ duration, avarageSpeed, travelledDistance }) => {
    let summary: any = { speed: +avarageSpeed, distance: +travelledDistance, duration: +duration }

    if (summary.distance) summary.distance = summary.distance.toFixed(1)
    if (summary.duration) {
        const result = moment.duration(+summary.duration, 'seconds').format('h[h] m[min]')

        summary.duration = result.replace('mins', 'min')
    }

    return summary
}

const TripsChart = ({ uniqueID, collection }: any): JSX.Element => {
    const { tripActive, trips } = useContext(TripsContext)
    const context: any = useContext(DashboardContext)
    const [chart, setChart] = useState<any>(null)
    const [options, setOptions] = useState<any>(null)
    const [isExpanded, setIsExpanded] = useState<any>(false)
    const [summary, setSummary] = useState<any>()
    const [summaryTrips, setSummaryTrips] = useState<any>()
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [timespanInformation, setTimespanInformation] = useState<any>({})

    let refChart: any = useRef()

    const getDuration = duration => {
        try {
            const aux = Math.trunc(duration)
            const result = moment.duration(aux, 'seconds').format('h[h] m[min]')

            return result.replace('mins', 'min')
        } catch (e) {
            return 'N/A'
        }
    }

    const initializeSummary = (uniqueID, span) => {
        if (!uniqueID) {
            setIsLoading(false)
            return
        }

        setIsLoading(true)

        getSummary(span, uniqueID, collection).then(response => {
            const data = response.data
            setTimespanInformation(data)

            if (!summaryTrips) {
                const options = timespanInformationToOptions(data?.speedList)
                setOptions(options)
                const summary = mountSummary(data)
                setSummary(summary)
                setSummaryTrips(summary)
            }

            setIsLoading(false)
        })
    }

    useEffect(() => {
        if (context?.params?.span) {
            initializeSummary(uniqueID, context.params.span)
        }
    }, [uniqueID, context])

    useEffect(() => {
        if (tripActive) {
            const { wayPoints = [] } = tripActive || {}
            const options = toChartOptions(wayPoints)
            setOptions(options)

            const loadSummary = async () => {
                const { travelledDistance, avarageSpeed, duration } = tripActive || {}
                const summary = {
                    distance: travelledDistance,
                    speed: avarageSpeed,
                    duration: getDuration(duration)
                }
                setSummary(summary)
            }

            loadSummary()
        } else {
            const options = timespanInformationToOptions(timespanInformation?.speedList)
            setOptions(options)
            setSummary(summaryTrips)
        }
    }, [tripActive])

    useEffect(() => {
        if (options && isLoading === false) {
            buildChart(options)
        }
    }, [options, isLoading])

    const buildChart = options => {
        const { series } = options

        if (options) {
            optionsChartLine.series = series
            
            const chartAux = {
                ...optionsChartLine,
                chart: { type: 'line', marginTop: 40, height: height, renderTo: refChart, reflow: true }
            }
            setChart(Highcharts.chart(chartAux))
        }
    }

    if(isLoading) return <WrapperLoading>
        <div className="loading" />
    </WrapperLoading>

    return (
        <ChartWrapper>
            <a className="hide-chart" onClick={() => setIsExpanded(!isExpanded)}>
                {i18n(isExpanded ? 'Hide' : 'Show')}
                <i className={`fas fa-caret-${isExpanded ? 'up' : 'down'}`}></i>
            </a>

            {!isExpanded && (
                <StyledSummary isExpanded={isExpanded}>
                    <li>{i18n(tripActive ? 'Trip Information' : 'Timespan Information')}</li>
                    <li>
                        <i className="far fa-road"></i>
                        {i18n('Distance')}
                        <span className="summary-value">{summary?.distance ? summary?.distance : '0'} Km</span>
                    </li>
                    <li>
                        <i className="far fa-clock"></i>
                        {i18n('Duration')}
                        <span className="summary-value">{summary?.duration}</span>
                    </li>
                    <li className="flex-1">
                        <i className="far fa-tachometer-alt-average"></i>
                        {i18n('Average Speed')}
                        <span className="summary-value">{summary?.speed} Km/h</span>
                    </li>
                </StyledSummary>
            )}

            <StyledSummaryExpanded isExpanded={isExpanded}>
                <StyledInfo>
                    <div>
                        <li className="info-title">{i18n('Path Information')}</li>
                        <li className="info-subtitle">
                            {i18n(tripActive ? 'Based on Selected trip' : 'Based on timestamp')}
                        </li>
                    </div>
                    <div className="info-block">
                        <li>
                            <StyledDetails>
                                <li>
                                    <div className="wrapper-icon">
                                        <i className="fas fa-road"></i>
                                    </div>
                                </li>
                                <li className="details-label">{i18n('Distance')}</li>
                                <li className="details-value">{summary?.distance ? summary?.distance : '0'}</li>
                                <li className="details-unit">km</li>
                            </StyledDetails>
                        </li>
                        <li>
                            <StyledDetails>
                                <li>
                                    <div className="wrapper-icon">
                                        <i className="fas fa-clock"></i>
                                    </div>
                                </li>
                                <li className="details-label">{i18n('Duration')}</li>
                                <li className="details-value">{summary?.duration}</li>
                            </StyledDetails>
                        </li>
                        <li>
                            <StyledDetails>
                                <li>
                                    <div className="wrapper-icon">
                                        <i className="fas fa-tachometer-alt-average"></i>
                                    </div>
                                </li>
                                <li className="details-label">{i18n('Average Speed')}</li>
                                <li className="details-value">{summary?.speed}</li>
                                <li className="details-unit">km/h</li>
                            </StyledDetails>
                        </li>
                    </div>
                </StyledInfo>

                <StyledChart id="chartContainer">
                    <div className="container-chart-title">
                        <div className="chart-title">{i18n('Vehicle Speed')}</div>
                    </div>

                    <div className="chart-subtitle">{i18n('Speed (Km/h)')}</div>

                    <div className="container-chart">
                        {options && <div ref={ref => (refChart = ref)} style={{ height: '100%', width: '100%' }} />}
                    </div>
                </StyledChart>
            </StyledSummaryExpanded>
        </ChartWrapper>
    )
}

export default TripsChart

const WrapperLoading = styled.div`
    height: 50px;
`

const ChartWrapper = styled.div`
    background: ${({ theme }) => theme['gray-0']};
    margin-bottom: 8px;
    border: 1px solid ${({ theme }) => theme['gray-400']};
    box-sizing: border-box;
    border-radius: 3px;
    z-index: 100;
    position: relative;

    .hide-chart {
        position: absolute;
        top: 15px;
        right: 16px;
        i {
            margin-left: 4px;
        }
    }
`

const StyledSummary = styled.ul`
    margin: 0;
    transition: height 0s ease-out;
    height: ${({ isExpanded }) => (isExpanded ? '0px' : '50px')};
    padding: ${({ isExpanded }) => (isExpanded ? '0' : '0 16px')};
    display: flex;
    overflow: hidden;

    .summary-value {
        font-style: normal;
        font-weight: 600;
        margin-left: 4px;
        color: ${({ theme }) => theme['gray-700']};
    }

    li {
        height: 100%;
        display: flex;
        align-items: center;
        font-size: 13px;
        padding: 0 16px;
        color: ${({ theme }) => theme['gray-600']};

        i {
            margin-right: 8px;
        }

        &:first-child {
            padding-right: 8px !important;
            font-style: normal;
            font-weight: 600;
            color: ${({ theme }) => theme['gray-800']} !important;
            border-right: 2px solid ${({ theme }) => theme['gray-300']};
        }

        &:first-child,
        &:last-child {
            padding: 0;
        }
    }
`

const StyledSummaryExpanded = styled.div`
    display: flex;
    height: ${({ isExpanded }) => (isExpanded ? '250px' : '0px')};
    transition: height 0.2s ease-out;
    overflow: hidden;
    padding: ${({ isExpanded }) => (isExpanded ? '16px' : '0px')};
`

const StyledInfo = styled.ul`
    width: 35%;
    min-width: 250px;
    border-right: 2px solid ${({ theme }) => theme['gray-300']};
    padding-right: 16px;
    margin: 0;
    display:flex;
    flex-direction:column;

    .info-title {
        font-weight: 600;
        font-size: 16px;
        color: ${({ theme }) => theme['gray-800']};
        margin-bottom: 4px;
    }

    .info-subtitle {
        font-weight: 600;
        font-size: 13px;
        color: ${({ theme }) => theme['gray-700']};
        margin-bottom: 12px;
    }
    .info-block{
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-around;
    }
`
const StyledDetails = styled.ul`
    display: flex;
    align-items: center;
    margin-bottom: 8px;

    .details-label {
        font-weight: 600;
        font-size: 11px;
        text-transform: uppercase;
        margin-left: 8px;
        margin-right: 4px;
        color: ${({ theme }) => theme['gray-700']};
    }

    .details-value {
        margin: 0 4px;
    }

    .details-value,
    .details-unit {
        font-weight: 600;
        font-size: 14px;
        color: ${({ theme }) => theme['gray-800']};
    }

    .wrapper-icon {
        height: 32px;
        width: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        color: ${({ theme }) => theme['gray-600']};
        background: ${({ theme }) => theme['gray-200']};
        border-radius:3px;

        i {
            font-weight: 400;
        }
    }
`

const StyledChart = styled.div`
    width: 75%;
    width: 100%;
    padding-left: 16px;
    margin: 0;
    overflow:visible;

    .container-chart {
        position: relative;
        top: -16px;
    }

    .container-chart-title {
        display: flex;
        align-items: center;
    }

    .chart-subtitle {
        font-weight: 600;
        font-size: 10.4712px;
        color: ${({ theme }) => theme['gray-800']};
        margin-top: 10px;
        z-index: 2;
        position: relative;
    }

    .chart-title {
        flex: 1;
        font-weight: 600;
        font-size: 14px;
        color: ${({ theme }) => theme['gray-800']};
    }
`
